// Added By Farhan Khan Khan on 24-Dec-2025
import React, { useState, useEffect } from "react";
import {
  Button,
  Col,
  Container,
  Form,
  Row,
  Tab,
  Tabs,
} from "react-bootstrap";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useLocation } from "react-router-dom";
import PubSub from "pubsub-js";
import { PREDEFINED_BOM } from "../../constants/bomPredefinedData";
// import { formatCurrency, formatNumber } from "../../utils/formatters";
import solisPowerApi from "../../api/solisPowerApi";
import { formatDate, getUserDetails } from "../../utils/helper";
import { handleUppercaseChange } from "../common/UpperCaseMethod";
import Helmet from "react-helmet";
import Main from "../layout/Main";
import BomSection from "./BomSection";
import QuotationLineItems from "./QuotationLineItems";
import QuotationBomItems from "./QuotationBomItems"; // Added By Farhan Khan Khan on 24-Dec-2025
import QuotationMountingStructures from "./QuotationMountingStructures"; // Created on 2026-02-07 by Farhan Khan - Mounting Structure tab
// QuotationFinancialItems removed - logic unified into QuotationLineItems
import Select from "react-select";
import moment from "moment";
import { v4 as uuidv4 } from "uuid";

// Default payment terms for new quotations (rich text HTML)
const DEFAULT_PAYMENT_TERMS = `<p><strong>PAYMENT TERMS</strong></p>
<ul>
<li>25% advance</li>
<li>70% before dispatch</li>
<li>5% on handover</li>
</ul>`;

const QuotationEdit = (props) => {
  const { id } = useParams();
  const mode = id ? "EDIT" : "CREATE"; // Added By System on 2026-03-19
  const location = useLocation();
  const parentId = location.state?.leadId;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams(); // Added on 2026-02-07 by Farhan Khan - Create Quotation Flow
  const [quotation, setQuotation] = useState({
    estimate_number: "",
    quotation_date: moment().format("YYYY-MM-DD"),
    expiry_date: moment().add(5, "days").format("YYYY-MM-DD"),
    customer_name: "",
    customer_mobile: "",
    customer_state: "",
    customer_city: "",
    subtotal: 0,
    total_discount: 0,
    tax_gst: 0,
    grand_total: 0,
    status: "Draft",
    lead_id: null,
    owner_ids: [], // Created on 2026-02-07 by Farhan Khan - Assigned Users (same pattern as Lead module)
    payment_terms: "", // Rich text; default applied on create only
    // Added on 2026-04-14 by Farhan Khan: Financial Summary State (renamed to subsidy_amount)
    subsidy_amount: -78000,
    project_cost: 0,
    gst_amount: 0,
    gst_type: "Inclusive",
    effective_price: 0,
    payable_amount: 0,
  });
  const [quotationLineItems, setQuotationLineItems] = useState([]); // Financial items (non_material)
  const [bomSections, setBomSections] = useState([]); // Non-financial items (material) - OLD BOM
  const [quotationBomItems, setQuotationBomItems] = useState([]); // Quotation-specific BOM items - NEW
  const [mountingStructureItems, setMountingStructureItems] = useState({ sections: [] }); // Reverted to object structure for UI compatibility (Bridge Approach)
  // Financial state is now handled within quotation and line items
  const [nonMaterialProductOptions, setNonMaterialProductOptions] = useState([]);
  const [materialProductOptions, setMaterialProductOptions] = useState([]);
  const [brandOptions, setBrandOptions] = useState([]);
  const [leadOptions, setLeadOptions] = useState([]);
  const [assignUsers, setAssignUsers] = useState([]); // Created on 2026-02-07 by Farhan Khan - Assigned Users dropdown
  const [validated, setValidated] = useState(false);
  const [activeTab, setActiveTab] = useState("quotation");
  // 🔥 ORIGINAL COPY FOR SAFE UPDATE (Issue 4) - Added By Farhan Khan on 31-Jan-2026
  const [originalMountingStructure, setOriginalMountingStructure] = useState([]);


  const [allLeads, setAllLeads] = useState([]);
  const [selectedLeadOption, setSelectedLeadOption] = useState(null);
  // Lead Name binding (Create Quotation from Lead View) - read-only display
  const [boundLead, setBoundLead] = useState(null);
  // Project options from lead_project_line_item (replaces Customer Name when lead selected)
  const [projectOptions, setProjectOptions] = useState([]);
  const [selectedProjectOption, setSelectedProjectOption] = useState(null);

  // UNDO / REDO STATE - Issue 2 Implementation
  // Added By Farhan Khan on 31-Jan-2026
  const [bomHistory, setBomHistory] = useState([]);
  const [bomFuture, setBomFuture] = useState([]);

  // Helper to save current state to history before making changes
  const saveToBomHistory = () => {
    setBomHistory(prev => [...prev, JSON.parse(JSON.stringify(quotationBomItems))]);
    setBomFuture([]); // Clear future on new action
  };

  const undoBomChange = () => {
    if (bomHistory.length === 0) return;
    const previous = bomHistory[bomHistory.length - 1];
    setBomFuture(prev => [JSON.parse(JSON.stringify(quotationBomItems)), ...prev]);
    setQuotationBomItems(previous);
    setBomHistory(prev => prev.slice(0, prev.length - 1));
  };

  // Added By System on 2026-03-19 - Auto generate quotation number
  useEffect(() => {
    const generateNumber = async () => {
      // Only run on CREATE mode when a lead is selected AND no number exists yet
      if (mode === "CREATE" && quotation.lead_id && leadOptions.length > 0 && !quotation.estimate_number) {
        try {
          // 1. Get Lead Code (First 3 letters)
          const selectedLead = leadOptions.find(opt => opt.value === quotation.lead_id);
          const leadName = selectedLead ? selectedLead.label : "SOL";
          const leadCode = leadName.substring(0, 3).trim().toUpperCase().padEnd(3, 'X');

          // 2. Get Auto Number (Global increment)
          const allQuotations = await solisPowerApi.fetchQuotations();
          let nextNumber = 1001;
          if (Array.isArray(allQuotations) && allQuotations.length > 0) {
            const numbers = allQuotations.map(q => {
              const match = q.estimate_number?.match(/\|(\d+)\|/);
              return match ? parseInt(match[1], 10) : 0;
            }).filter(n => n > 0);

            if (numbers.length > 0) {
              nextNumber = Math.max(...numbers) + 1;
            }
          }
          const autoNumber = nextNumber.toString().padStart(5, '0');

          // 3. Get Time (hh:mm format)
          const now = new Date();
          const hh = String(now.getHours() % 12 || 12).padStart(2, '0');
          const mm = String(now.getMinutes()).padStart(2, '0');
          const timeStr = `${hh}:${mm}`;

          // 4. Get Version (Quotation count per Lead)
          const leadQuotations = Array.isArray(allQuotations) ? allQuotations.filter(q => q.lead_id === quotation.lead_id) : [];
          const version = leadQuotations.length + 1;

          const newNumber = `${leadCode} |${autoNumber}| SOL |${timeStr}| V${version}`;

          setQuotation(prev => ({
            ...prev,
            estimate_number: newNumber
          }));
        } catch (error) {
          console.error("Error generating quotation number:", error);
        }
      }
    };

    generateNumber();
  }, [quotation.lead_id, leadOptions, mode]);

  const redoBomChange = () => {
    if (bomFuture.length === 0) return;
    const next = bomFuture[0];
    setBomHistory((prev) => [...prev, JSON.parse(JSON.stringify(quotationBomItems))]);
    setQuotationBomItems(next);
    setBomFuture((prev) => prev.slice(1));
  };

  // PART 1: DEFAULT ROW (FINANCIAL LINE ITEMS) - Added on 26-Mar-2026 by Farhan Khan
  // Automatically adds a default Rooftop Solar row when creating a new quotation
  useEffect(() => {
    if (mode === "CREATE" && quotationLineItems.length === 0) {
      setQuotationLineItems([
        {
          id: `temp_${Date.now()}`,
          product_id: null,
          product_name: "Rooftop Solar Power Plant",
          product_description: "",
          quantity: "",
          unit_price: "",
          discount: 0,
          gross_amount: 0,
          net_amount: 0,
        },
      ]);
    }
  }, [mode, quotationLineItems.length]);

  // Keyboard Shortcuts for Undo/Redo - Issue 2
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Only trigger if in BOM tab or Mounting tab (though currently only BOM implemented)
      if (activeTab !== 'bom') return;

      if (e.ctrlKey && e.key.toLowerCase() === 'z') {
        if (e.shiftKey) {
          e.preventDefault();
          redoBomChange();
        } else {
          e.preventDefault();
          undoBomChange();
        }
      } else if (e.ctrlKey && e.key.toLowerCase() === 'y') {
        e.preventDefault();
        redoBomChange();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [bomHistory, bomFuture, quotationBomItems, activeTab]);

  console.log("first location", location)

  // Created on 2026-02-07 by Farhan Khan - Default Mounting Structure Import
  // Automatically imports master mounting structures on quotation create/edit page load
  const loadDefaultMountingStructures = async () => {
    try {
      // Only load if mountingStructureItems is empty (to avoid overwriting existing data on edit)
      if (mountingStructureItems && mountingStructureItems.length > 0) {
        console.log('[QuotationEdit] Mounting structures already loaded, skipping default import');
        return;
      }

      console.log('[QuotationEdit] Loading default mounting structures...');
      const mappedMountingItems = await fetchMasterMountingStructuresMapped(null);
      if (mappedMountingItems.length === 0) return;
      setMountingStructureItems(mappedMountingItems);
      PubSub.publish("RECORD_SAVED_TOAST", {
        title: "Info",
        message: `${mappedMountingItems.length} mounting structure(s) imported automatically`,
      });
    } catch (error) {
      console.error('[QuotationEdit] Error loading default mounting structures:', error);
    }
  };



  // Helper: fetch master mounting structures, dedupe, optionally exclude existing, map to frontend format.
  // excludeMasterIds: Set of mounting_structure.id to skip (for update mode - only import new master records).
  // Returns array of mapped items (may be empty).
  const fetchMasterMountingStructuresMapped = async (excludeMasterIds = null) => {
    const categories = await solisPowerApi.fetchActiveBomCategories();
    if (!categories || !Array.isArray(categories)) return [];
    const mountingCategories = categories.filter(cat => cat.bom_type === 'MOUNTING_STRUCTURE');
    if (mountingCategories.length === 0) return [];

    const allMountingStructures = [];
    for (const category of mountingCategories) {
      try {
        const structures = await solisPowerApi.fetchMountingStructuresByCategory(category.id);
        if (structures && Array.isArray(structures) && structures.length > 0) {
          allMountingStructures.push(...structures);
        }
      } catch (error) {
        console.error(`[QuotationEdit] Error fetching structures for category ${category.id}:`, error);
      }
    }
    if (allMountingStructures.length === 0) return [];

    const seenMasterIds = new Set();
    const unique = allMountingStructures.filter((s) => {
      if (seenMasterIds.has(s.id)) return false;
      seenMasterIds.add(s.id);
      return true;
    });
    const toMap = excludeMasterIds && excludeMasterIds.size > 0
      ? unique.filter((s) => !excludeMasterIds.has(s.id))
      : unique;

    // Fetch parts for each structure (parts are in separate table)
    const structuresWithParts = await Promise.all(toMap.map(async (s) => {
      try {
        const parts = await solisPowerApi.fetchMountingStructurePartsByStructure(s.id);
        return { ...s, parts: parts || [] };
      } catch { return { ...s, parts: [] }; }
    }));

    // Sort by s_no (ORDER BY s_no ASC)
    structuresWithParts.sort((a, b) => (a.s_no ?? 9999) - (b.s_no ?? 9999));

    const normalizeRoofType = (roofType) => {
      if (!roofType) return null;
      const rawValue = roofType?.value || roofType;
      if (!rawValue || (typeof rawValue === 'object' && Object.keys(rawValue).length === 0)) return null;
      const normalized = rawValue?.toString().trim().toUpperCase().replace(/\s+/g, '');
      const validTypes = ['RCC', 'GROUND', 'TINSHED', 'COMBO'];
      return validTypes.includes(normalized) ? normalized : null;
    };
    const normalizeParts = (parts, structureInfo) => {
      if (!parts || !Array.isArray(parts)) return [];
      return parts
        .filter(part => part != null)
        .map(part => {
          let normalizedShape = Array.isArray(part.shape) ? part.shape.filter(s => s) : (part.shape && typeof part.shape === 'string' ? part.shape.split(',').map(s => s.trim()).filter(s => s) : []);
          let normalizedMake = Array.isArray(part.make) ? part.make.filter(m => m) : (part.make && typeof part.make === 'string' ? part.make.split(',').map(m => m.trim()).filter(m => m) : []);
          return {
            ...part,
            component_name: (part.component_name || part.part_name || part.item_name || "").trim(),
            item_name: (part.item_name || part.component_name || part.part_name || "").trim(),
            specs: part.specs || part.specification || "",
            specification: part.specification || part.specs || "",
            size: part.size || "",
            shape: normalizedShape,
            make: normalizedMake,
            others: part.others || "",
            special_structure_type: part.special_structure_type || structureInfo.special_structure_type || "",
            special_structure_specs: part.special_structure_specs || structureInfo.special_structure_specs || part.specification || part.specs || "",
            special_structure_length: part.special_structure_length ?? structureInfo.special_structure_length ?? null,
          };
        })
        .filter(part => (part.component_name && part.component_name.trim()) || (part.item_name && part.item_name.trim()));
    };

    // EXACT same fields as MountingStructureForm addLineItem - 1:1 mapping, ORDER BY s_no ASC
    return structuresWithParts.map((structure, index) => {
      const normalizedRoofType = normalizeRoofType(structure.roof_type);
      const normalizedParts = normalizeParts(structure.parts || [], structure);
      return {
        ...structure,
        id: `temp_${Date.now()}_${index}`,
        mounting_structure_id: structure.id,
        s_no: structure.s_no ?? (index + 1),
        item_type: structure.item_type || "COMPONENT",
        roof_type: normalizedRoofType || "",
        structure_height_front: structure.structure_height_front != null ? structure.structure_height_front : null,
        structure_height_back: structure.structure_height_back != null ? structure.structure_height_back : null,
        parts: normalizedParts,
        tin_shed_structure_type: structure.tin_shed_structure_type || null,
        tin_shed_mounting_type: structure.tin_shed_mounting_type || null,
        combo_panels_rcc: structure.combo_panels_rcc != null ? structure.combo_panels_rcc : null,
        combo_panels_ground: structure.combo_panels_ground != null ? structure.combo_panels_ground : null,
        combo_panels_tinshed: structure.combo_panels_tinshed != null ? structure.combo_panels_tinshed : null,
        special_structure_type: structure.special_structure_type || null,
        special_structure_specs: structure.special_structure_specs || structure.specs || "",
        special_structure_length: structure.special_structure_length != null ? structure.special_structure_length : (structure.length != null ? structure.length : null),
        cleaning_system: structure.notes || "",
        others_text: structure.others_text ?? "",
        status: structure.status || "Active"
      };
    });
  };

  // Created on 2026-02-07 by Farhan Khan - Default Mounting Structure Import (create mode: all master records once)
  // const loadDefaultMountingStructures = async () => {
  //   try {
  //     if (mountingStructureItems && mountingStructureItems.length > 0) {
  //       console.log('[QuotationEdit] Mounting structures already loaded, skipping default import');
  //       return;
  //     }
  //     console.log('[QuotationEdit] Loading default mounting structures...');
  //     const mappedMountingItems = await fetchMasterMountingStructuresMapped(null);
  //     if (mappedMountingItems.length === 0) return;
  //     setMountingStructureItems(mappedMountingItems);
  //     PubSub.publish("RECORD_SAVED_TOAST", {
  //       title: "Info",
  //       message: `${mappedMountingItems.length} mounting structure(s) imported automatically`,
  //     });
  //   } catch (error) {
  //     console.error('[QuotationEdit] Error loading default mounting structures:', error);
  //   }
  // };

  // Created on 2026-02-07 by Farhan Khan - Get default owners function (same as Lead module)
  const getDefaultOwners = (resultedUsers, userInfo) => {
    const loggedInUser = resultedUsers.find((u) => u.id === userInfo.id);

    let selected = [];

    // Add logged-in user ONLY (Prevent random telecaller selection)
    if (loggedInUser) {
      selected.push({
        value: loggedInUser.id,
        label: loggedInUser.username,
      });
    }

    return selected;
  };

  // Created on 2026-02-07 by Farhan Khan - Fix infinite API calls (separate useEffect for users)
  useEffect(() => {
    async function fetchUsers() {
      const resultedUsers = await solisPowerApi.fetchUsersDropdown();
      if (resultedUsers && Array.isArray(resultedUsers)) {
        setAssignUsers(resultedUsers);
      }
    }
    fetchUsers();
  }, []); // Empty dependency array - fetch users only once on mount

  // Created on 2026-02-07 by Farhan Khan - Fix infinite API calls (main init useEffect)
  useEffect(() => {
    async function init() {
      // Guard: prevent execution if id is invalid
      if (id && (id === 'undefined' || id === 'null' || !id)) {
        console.warn('[QuotationEdit] Invalid id, skipping init:', id);
        return;
      }

      await fetchNonMaterialProducts();
      await fetchMaterialProducts();
      await fetchActiveBrands();
      await fetchLeadsForQuotation();

      if (id) {
        // Editing existing quotation
        try {
          const quotationData = await solisPowerApi.fetchQuotationById(id);
          if (quotationData) {
            console.log("API RESPONSE (ALL ITEMS):", quotationData.items);
            console.log("API RESPONSE (FULL DATA):", quotationData);

            // BUG FIX: Convert PostgreSQL DECIMAL strings to numbers
            // Added By Farhan Khan Khan on 24-Dec-2025
            // WHY IT WAS FAILING: PostgreSQL DECIMAL fields return as strings, causing .toFixed() errors
            // WHAT WAS FIXED: Parse string values to numbers when loading quotation data

            // Created on 2026-02-07 by Farhan Khan - Process owner_ids from API (same pattern as Lead module)
            let processedOwnerIds = [];
            if (quotationData.owner_ids && Array.isArray(quotationData.owner_ids)) {
              // API returns array of objects with {value, label} format
              processedOwnerIds = quotationData.owner_ids;
            } else if (quotationData.owner_ids && typeof quotationData.owner_ids === 'string') {
              // Handle case where owner_ids might be a JSON string
              try {
                const parsed = JSON.parse(quotationData.owner_ids);
                processedOwnerIds = Array.isArray(parsed) ? parsed : [];
              } catch (e) {
                processedOwnerIds = [];
              }
            }

            setQuotation(prev => ({
              ...prev,
              id: quotationData.id,
              estimate_number: quotationData.estimate_number || "",
              quotation_date: quotationData.quotation_date || moment().format("YYYY-MM-DD"),
              expiry_date: quotationData.expiry_date || moment().add(5, "days").format("YYYY-MM-DD"),
              customer_name: quotationData.customer_name || quotationData.lead?.name || "",
              customer_mobile: quotationData.customer_mobile || quotationData.lead?.phone || "",
              customer_state: quotationData.customer_state || quotationData.lead?.state || "",
              customer_city: quotationData.customer_city || quotationData.lead?.city || "",
              subtotal: parseFloat(quotationData.subtotal || 0),
              total_discount: parseFloat(quotationData.total_discount || 0),
              tax_gst: parseFloat(quotationData.tax_gst || 0),
              grand_total: parseFloat(quotationData.grand_total || 0),
              status: quotationData.status || "Draft",
              lead_id: quotationData.lead_id || null,
              owner_ids: processedOwnerIds,
              payment_terms: quotationData.payment_terms ?? "",

              // Added on 2026-04-14 by Farhan Khan: Hydrate Financial Fields from DB
              // Extract subsidy_amount explicitly prioritising line item (where DB typically saves it), else root
              subsidy_amount: (quotationData.items?.find(i => i.unit_price != null)?.subsidy_amount) ?? quotationData.subsidy_amount ?? -78000,
              project_cost: parseFloat(quotationData.project_cost || 0),
              gst_amount: parseFloat(quotationData.gst_amount || 0),
              gst_type: quotationData.gst_type || "Inclusive",
              effective_price: parseFloat(quotationData.effective_price || 0),
              payable_amount: parseFloat(quotationData.payable_amount || 0),
            }));

            // Split items: non_material → Quotation tab, material → BOM tab
            const rawItems = (quotationData.line_items || quotationData.items || []);
            console.log("EDIT API LINE ITEMS:", rawItems);

            if (rawItems.length > 0) {
              const financialItems = rawItems.filter(item => item.unit_price != null).map(item => ({
                ...item, // 👈 PRESERVE ALL FIELDS (subsidy_amount, etc.)
                phase_qty: item.phase_qty || item.phase || "", // Map DB phase_qty to UI state
                id: item.id || `temp_${Date.now()}_${Math.random()}`,
              }));
              console.log("STATE LINE ITEMS (HYDRATED):", financialItems);
              const materialItems = [];

              // Process items after products are loaded
              for (const item of rawItems) {
                // Check if product has pricing (financial item) or not (BOM item)
                // If unit_price is not null, it's a financial item
                if (item.unit_price === null || item.unit_price === undefined) {
                  // Material item (BOM) - no pricing
                  const toArr = (v) => !v ? [] : Array.isArray(v) ? v : [v];
                  materialItems.push({
                    id: item.id || `item_${Date.now()}`,
                    product_id: item.product_id,
                    product_name: item.product_name,
                    product_description: item.product_description || "",
                    quantity: item.quantity || "",
                    brand_id: toArr(item.brand_id),
                    unit: item.unit || "Nos",
                  });
                }
              }

              setQuotationLineItems(financialItems);

              // Rebuild BOM sections from material items
              if (materialItems.length > 0) {
                rebuildBomSectionsFromItems(materialItems);
              }

              // Added By Farhan Khan Khan on 24-Dec-2025
              // Load quotation_bom_items if they exist
              if (quotationData.quotation_bom_items && quotationData.quotation_bom_items.length > 0) {
                // Created on 2026-02-07 by Farhan Khan - Separate STANDARD BOM from Mounting Structure
                const parseJsonArrayField = (field) => {
                  if (!field) return [];
                  if (Array.isArray(field)) return field;
                  if (typeof field === 'string') {
                    try {
                      const parsed = JSON.parse(field);
                      return Array.isArray(parsed) ? parsed : [parsed];
                    } catch {
                      return field.includes(',') ? field.split(',').map(s => s.trim()).filter(Boolean) : [field];
                    }
                  }
                  return [field];
                };
                const standardBomItems = quotationData.quotation_bom_items
                  .filter(item =>
                    !item.display_category || !item.display_category.toLowerCase().includes('mounting')
                  )
                  .map(item => {
                    const formatDisplayValue = (val) => {
                      if (!val) return "";
                      if (Array.isArray(val)) return val.join(", ");
                      if (typeof val === "string" && val.includes("{")) {
                        return val.replace(/[{}"]/g, "").split(",").map(s => s.trim()).filter(Boolean).join(", ");
                      }
                      return val;
                    };
                    return {
                      ...item,
                      category: parseJsonArrayField(item.category),
                      make: parseJsonArrayField(item.make),
                      specs: parseJsonArrayField(item.specs),
                      size_range_capacity: formatDisplayValue(item.size_range_capacity),
                    };
                  });
                setQuotationBomItems(standardBomItems);
              }
            }

            // 💥 FIX: Mounting Structure - Safe Hydration (Single Source of Truth)
            if (quotationData.id) {
              try {
                const res = await solisPowerApi.fetchQuotationMountingStructures(quotationData.id);
                console.log("FINAL API RESPONSE", res);

                // Support both legacy 'sections' and new 'mounting_structures' keys
                const structures = res.mounting_structures || res.sections;

                if (res && Array.isArray(structures)) {
                  setMountingStructureItems({ sections: structures });
                  setOriginalMountingStructure([...structures]);
                } else {
                  setMountingStructureItems({ sections: [] });
                  setOriginalMountingStructure([]);
                }
              } catch (error) {
                console.error('[QuotationEdit] Error fetching mounting structures:', error);
                setMountingStructureItems({ sections: [] });
                setOriginalMountingStructure([]);
              }
            } else {
              setMountingStructureItems({ sections: [] });
              setOriginalMountingStructure([]);
            }
          }
        } catch (error) {
          console.error("Error fetching quotation:", error);
        }
      } else {
        // Create mode: auto-select default owners and auto-import mounting structures from master
        const userInfo = getUserDetails();

        if (assignUsers && assignUsers.length > 0) {
          const defaultOwners = getDefaultOwners(assignUsers, userInfo);
          // Standardized: Only auto-assign if no lead is selected AND no owners already exist
          // Use functional update to avoid stale closure on quotation state
          setQuotation(prev => {
            if (!prev.lead_id && (!prev.owner_ids || prev.owner_ids.length === 0)) {
              return { ...prev, owner_ids: defaultOwners };
            }
            return prev;
          });
        } else {
          solisPowerApi.fetchUsersDropdown().then(resultedUsers => {
            if (resultedUsers && Array.isArray(resultedUsers)) {
              setAssignUsers(resultedUsers);
              const defaultOwners = getDefaultOwners(resultedUsers, userInfo);
              setQuotation(prev => {
                if (!prev.lead_id && (!prev.owner_ids || prev.owner_ids.length === 0)) {
                  return { ...prev, owner_ids: defaultOwners };
                }
                return prev;
              });
            }
          });
        }
        try {
          await loadDefaultMountingStructures();
        } catch (err) {
          console.error('[QuotationEdit] Error loading default mounting structures:', err);
        }
        // Default payment_terms on create only (do not overwrite if user edits)
        setQuotation(prev => ({
          ...prev,
          payment_terms: (prev.payment_terms && prev.payment_terms.trim()) ? prev.payment_terms : DEFAULT_PAYMENT_TERMS,
        }));
      }
    }
    init();
  }, [id]); // Fixed on 2026-02-07 by Farhan Khan - Remove assignUsers from dependencies to prevent infinite loop

  const fetchNonMaterialProducts = async () => {
    const products = await solisPowerApi.fetchNonMaterialProducts();
    if (products) {
      const options = products.map(product => ({
        value: product.id,
        label: product.name,
        product: product
      }));
      setNonMaterialProductOptions(options);
    }
  };

  const fetchMaterialProducts = async () => {
    const products = await solisPowerApi.fetchMaterialProducts();
    if (products) {
      const options = products.map(product => ({
        value: product.id,
        label: product.name,
        product: product
      }));
      setMaterialProductOptions(options);
    }
  };

  const fetchActiveBrands = async () => {
    const brands = await solisPowerApi.fetchActiveBrands();
    if (brands) {
      const options = brands.map(brand => ({
        value: brand.id,
        label: brand.name
      }));
      setBrandOptions(options);
    }
  };

  const fetchLeadsForQuotation = async () => {
    const leads = await solisPowerApi.fetchLeadsForQuotation();
    if (leads) {
      const options = leads.map(lead => ({
        value: lead.id,
        label: `${lead.lead_name || ""}${lead.enquiry_no ? ` (${lead.enquiry_no})` : ""}`,
        lead: lead
      }));
      setLeadOptions(options);

      const leadIdFromUrl = searchParams?.get('lead_id');
      if (!id && leadIdFromUrl && options.length > 0) {
        const foundOption = options.find(l => l.value === parseInt(leadIdFromUrl));
        if (foundOption) {
          setTimeout(() => {
            handleLeadChange(foundOption);
          }, 0);
        }
      }
    }
  };

  // Created on 2026-03-24 by Farhan Khan - Auto-select default product for solar quotations
  useEffect(() => {
    if (!id && nonMaterialProductOptions.length > 0 && quotationLineItems.length === 0) {
      const rooftopOption = nonMaterialProductOptions.find(o =>
        o.label.toLowerCase().includes("rooftop solar")
      );
      if (rooftopOption) {
        const selectedProd = rooftopOption.product;
        const capMatch = selectedProd.name?.match(/(\d+)\s*(KW|kw)/i);
        const capacity = capMatch ? capMatch[1] : (selectedProd.description?.match(/Capacity:\s*([^,]+)/)?.[1] || "1");
        const rate = selectedProd.product_unit_price || 0;

        const newItem = {
          id: `temp_${Date.now()}`,
          product_id: rooftopOption.value,
          product_name: rooftopOption.label,
          product_description: selectedProd.description || "",
          quantity: capacity,
          unit_price: rate,
          discount: 0,
          gross_amount: (parseFloat(capacity) || 1) * (parseFloat(rate) || 0),
          net_amount: (parseFloat(capacity) || 1) * (parseFloat(rate) || 0)
        };
        setQuotationLineItems([newItem]);

        // Initialize quotation header with solar defaults
        setQuotation(prev => ({
          ...prev,
          gst_type: prev.gst_type || "Inclusive",
          subsidy_amount: prev.subsidy_amount ?? -78000
        }));
      }
    }
  }, [nonMaterialProductOptions, id]);
  //Added By Jaseem 10-2-2026
  useEffect(() => {
    initLeads();
  }, [parentId]);




  useEffect(() => {
    initLeads();
  }, [parentId, quotation?.lead_id, searchParams, id]);

  async function initLeads() {
    try {
      const resultedLeads = await solisPowerApi.fetchLeadsForQuotation();
      if (!Array.isArray(resultedLeads)) return;

      setAllLeads(resultedLeads);

      let options = resultedLeads.map(l => ({
        value: l.id,
        label: buildLeadLabel(l),
        lead: l
      }));

      // ---- EDIT CASE ---- Ensure quotation's lead is in dropdown (it may have different status e.g. Quotation Shared)
      if (id && quotation?.lead_id) {
        let found = options.find(
          o => String(o.value) === String(quotation.lead_id)
        );

        if (!found) {
          try {
            const leadData = await solisPowerApi.fetchLeadById(quotation.lead_id);
            if (leadData) {
              const extraOpt = {
                value: leadData.id,
                label: buildLeadLabel(leadData),
                lead: leadData
              };
              found = extraOpt;
              options = [extraOpt, ...options];
            }
          } catch (e) {
            console.warn("[QuotationEdit] Could not fetch quotation lead for dropdown:", e);
          }
        }

        setLeadOptions(options);
        if (found) handleLeadChange(found);
        return;
      }

      setLeadOptions(options);

      // ---- CREATE CASE ---- (support lead_id from URL or location.state)
      const leadIdForCreate = searchParams?.get("lead_id") || parentId;
      if (leadIdForCreate) {
        const found = options.find(
          o => String(o.value) === String(leadIdForCreate)
        );

        if (found) handleLeadChange(found);
      }

    } catch (err) {
      console.error("initLeads failed:", err);
    }
  }



  function buildLeadLabel(lead) {
    return `${lead.lead_name || ""}${lead.enquiry_no ? ` (${lead.enquiry_no})` : ""
      }`;
  }



  const handleLeadChange = async (selectedOption, actionMeta) => {
    setSelectedLeadOption(selectedOption || null);
    setSelectedProjectOption(null);

    // Detect if this change was triggered by the user interacting with the UI
    const isManualChange = actionMeta !== undefined;

    if (!selectedOption) {
      setQuotation(prev => ({
        ...prev,
        lead_id: null,
        customer_name: "",
        customer_mobile: "",
        customer_state: "",
        customer_city: "",
        ...(isManualChange && { owner_ids: [] }) // Clear assigned user if lead cleared manually
      }));
      return;
    }

    const leadId = selectedOption.value ?? null;
    let lead = selectedOption.lead || {};

    // Auto-fetch full lead details to ensure state & city are populated (Fix: state/city may be missing from dropdown)
    try {
      const fullLead = await solisPowerApi.fetchLeadById(leadId);
      if (fullLead) {
        lead = fullLead;
      }
    } catch (e) {
      console.warn("[QuotationEdit] Could not fetch lead details for state/city:", e);
    }

    setQuotation(prev => {
      const newState = {
        ...prev,
        lead_id: leadId,
        customer_name: quotation?.id ? prev.customer_name : "",
        customer_mobile:
          lead.phone?.trim() ||
          lead.whatsapp_no?.trim() ||
          "",
        customer_state: lead.state || lead.customer_state || "",
        customer_city: lead.city || lead.customer_city || "",
      };

      // Auto-populate Assigned User (owner_ids) on manual lead selection OR on Create mode
      if (isManualChange || mode === "CREATE") {
        // Standardized Assigned User resolution - Added By System on 2026-04-29
        // Resolves from lead.assigned_user or lead.owner_ids array or lead.owner_id
        let resolvedOwners = [];
        if (lead.owner_ids && Array.isArray(lead.owner_ids) && lead.owner_ids.length > 0) {
          resolvedOwners = lead.owner_ids.filter(o => o && o.value);
        } else if (lead.assigned_user) {
          resolvedOwners = [lead.assigned_user];
        } else if (lead.owner_id) {
          resolvedOwners = [{ value: lead.owner_id, label: lead.owner_name || "Lead Owner" }];
        }

        if (resolvedOwners.length > 0) {
          newState.owner_ids = resolvedOwners;
        }
      }

      return newState;
    });
  };

  const handleProjectChange = (opt) => {
    setSelectedProjectOption(opt || null);
    const displayValue = opt?.label || "";
    const project = opt?.project || {};
    const capacity = project.plant_capacity_required_by_client || "";
    const connectionType = project.type_of_connection || "";

    setQuotation(prev => ({ ...prev, customer_name: displayValue }));

    // Auto-populate fields in the first solar line item
    if (quotationLineItems.length > 0) {
      setQuotationLineItems(prev => {
        const newItems = [...prev];
        if (newItems[0]) {
          // Map Plant Capacity
          if (capacity) newItems[0].plant_capacity_required_by_client = capacity;

          // Map Phase from Project's type_of_connection
          if (connectionType) {
            newItems[0].phase_qty = connectionType;
          }

          // Re-calculate line totals for the first item if capacity changed
          if (capacity) {
            const price = parseFloat(newItems[0].unit_price) || 0;
            const disc = parseFloat(newItems[0].discount) || 0;
            newItems[0].gross_amount = (parseFloat(capacity) || 0) * price;
            newItems[0].net_amount = newItems[0].gross_amount - disc;
          }
        }
        return newItems;
      });
    }
  };



  //============================
  // Added on 2026-02-07 by Farhan Khan - Create Quotation Flow
  // Effect to handle lead_id from URL params (fix: use String comparison for UUID)
  useEffect(() => {
    const leadIdFromUrl = searchParams.get('lead_id');
    if (!id && leadIdFromUrl && leadOptions.length > 0) {
      const foundOption = leadOptions.find(l => String(l.value) === String(leadIdFromUrl));
      if (foundOption && !quotation.lead_id) {
        handleLeadChange(foundOption);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadOptions, id, quotation.lead_id]);

  // Part 1: Fetch bound lead for Create Quotation from Lead View - supports page reload
  useEffect(() => {
    const leadIdFromUrl = searchParams.get("lead_id");
    const leadId = leadIdFromUrl || quotation?.lead_id;
    if (!leadId || id) return;
    let cancelled = false;
    (async () => {
      try {
        const leadData = await solisPowerApi.fetchLeadById(leadId);
        if (!cancelled && leadData) {
          setBoundLead(leadData);
          setQuotation((prev) =>
            leadIdFromUrl && !prev.lead_id ? { ...prev, lead_id: leadId } : prev
          );
        }
      } catch (e) {
        if (!cancelled) setBoundLead(null);
      }
    })();
    return () => { cancelled = true; };
  }, [id, quotation?.lead_id]);

  // Part 2: Fetch project line items when lead is selected
  useEffect(() => {
    const leadId = quotation?.lead_id;
    if (!leadId) {
      setProjectOptions([]);
      setSelectedProjectOption(null);
      return;
    }
    let cancelled = false;
    solisPowerApi.fetchLeadProjectLineItems(leadId).then((rows) => {
      if (cancelled) return;
      const items = Array.isArray(rows) ? rows : [];
      const opts = items.map((item, idx) => {
        const label = [item.site_name, item.address, `${item.city || ''} ${item.state || ''}`.trim()]
          .filter(Boolean)[0] || `Project ${idx + 1}`;
        return { value: item.id, label, project: item };
      });
      const custName = quotation?.customer_name?.trim();
      const match = custName ? opts.find(o => o.label === custName) : null;
      const customOpt = custName && !match ? [{ value: "custom", label: custName }] : [];
      setProjectOptions([...opts, ...customOpt]);
      setSelectedProjectOption(match || (custName ? { value: "custom", label: custName } : null));
    });
    return () => { cancelled = true; };
  }, [quotation?.lead_id]);

  const rebuildBomSectionsFromItems = (items) => {
    const sections = [];
    const sectionMap = {};

    items.forEach((item, index) => {
      const sectionId = "default_section";

      if (!sectionMap[sectionId]) {
        sectionMap[sectionId] = {
          id: sectionId,
          categoryId: null,
          categoryName: "Items",
          displayNumber: "1",
          isExpanded: true,
          items: []
        };
      }

      const toBrandIdsArr = (v) => !v ? [] : Array.isArray(v) ? v : [v];
      sectionMap[sectionId].items.push({
        id: item.id || `item_${index}`,
        product_id: item.product_id,
        product_name: item.product_name,
        product_description: item.product_description || "",
        quantity: item.quantity || "",
        brand_id: toBrandIdsArr(item.brand_id),
        unit: item.unit || "Nos"
      });
    });

    setBomSections(Object.values(sectionMap));
  };

  const handleQuotationChange = (e) => {
    const { name, value } = e.target;
    setQuotation(prev => ({ ...prev, [name]: value }));
  };

  // const handleLeadChange = async (selectedOption) => {
  //   if (selectedOption && selectedOption.lead) {
  //     const lead = selectedOption.lead;
  //     setQuotation({
  //       ...quotation,
  //       lead_id: selectedOption.value,
  //       customer_name: lead.lead_name || "",
  //       customer_mobile: lead.phone || lead.whatsapp_no || "",
  //       customer_state: lead.state || "",
  //       customer_city: lead.city || "",
  //     });
  //   } else {
  //     setQuotation({
  //       ...quotation,
  //       lead_id: null,
  //       customer_name: "",
  //       customer_mobile: "",
  //       customer_state: "",
  //       customer_city: "",
  //     });
  //   }
  // };

  // Financial totals are now computed within QuotationLineItems and synced back to header state

  const checkRequiredFields = () => {
    // 1. Lead Validation
    if (!quotation.lead_id) {
      PubSub.publish("RECORD_ERROR_TOAST", {
        title: "Error",
        message: "Lead is required",
      });
      return true;
    }

    // 2. Project Validation
    if (!quotation.customer_name || String(quotation.customer_name).trim() === "") {
      PubSub.publish("RECORD_ERROR_TOAST", {
        title: "Error",
        message: "Project is required",
      });
      return true;
    }

    // 3. Financial Line Items Validation
    if (!quotationLineItems || quotationLineItems.length === 0) {
      PubSub.publish("RECORD_ERROR_TOAST", {
        title: "Error",
        message: "Financial Line Item is required",
      });
      return true;
    }

    // Deep validation for Line Items (Product and Capacity)
    const isLineItemInvalid = quotationLineItems.some(item =>
      !item.product_id ||
      (!item.plant_capacity_required_by_client && !item.quantity)
    );

    if (isLineItemInvalid) {
      PubSub.publish("RECORD_ERROR_TOAST", {
        title: "Error",
        message: "Financial Line Item is required",
      });
      return true;
    }

    // 4. Estimate Number (Check only on Edit)
    if (quotation.id && (!quotation.estimate_number || String(quotation.estimate_number).trim() === "")) {
      PubSub.publish("RECORD_ERROR_TOAST", {
        title: "Error",
        message: "Estimate Number is required",
      });
      return true;
    }

    return false;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (checkRequiredFields()) {
      setValidated(true);
      return;
    }

    // Collect all items from both tabs
    console.log("BOM STATE BEFORE SAVE", quotationBomItems)

    const allItems = [];

    // Add financial items from Quotation tab
    quotationLineItems.forEach(item => {
      const cap = parseFloat(item.plant_capacity_required_by_client) || 0;
      const qty = parseFloat(item.quantity) || 0;
      const price = parseFloat(item.unit_price) || 0;
      const discount = parseFloat(item.discount) || 0;
      const grossAmount = cap * price;
      const netAmount = grossAmount - discount;

      allItems.push({
        product_id: item.product_id,
        product_description: item.product_description || "",
        plant_capacity_required_by_client: cap,
        quantity: qty,
        unit_price: price,
        discount: discount,
        brand_id: null,
        unit: null,
        gross_amount: grossAmount,
        net_amount: netAmount,
        phase_qty: item.phase || item.phase_qty || "", // UI phase -> DB phase_qty
        subsidy_amount: (quotation.subsidy_amount ?? item.subsidy_amount ?? -78000) // Prioritize edited root quotation state
      });
    });

    // Add non-financial items from BOM tab
    bomSections.forEach(section => {
      section.items.forEach(item => {
        allItems.push({
          product_id: item.product_id,
          product_description: item.product_description || "",
          quantity: item.quantity || "",
          unit_price: null,
          discount: null,
          brand_id: item.brand_id || null,
          unit: item.unit || "Nos",
          gross_amount: null,
          net_amount: null,
          phase_qty: item.phase || item.phase_qty || "" // UI phase -> DB phase_qty
        });
      });
    });

    // Estimate number: auto-generated on backend during create (format: AAA | 12345 | BBB | dd:mm | V{version})
    // Send empty string when creating; backend will generate
    let estimateNumber = quotation.id ? quotation.estimate_number : (quotation.estimate_number || "").trim() || "";

    // Created on 2026-02-07 by Farhan Khan - Filter empty rows before saving
    const validBomItems = quotationBomItems.filter(item =>
      (item.item_name && item.item_name.trim()) ||
      (item.description && item.description.trim()) ||
      (item.bom_master_item_id !== null && item.bom_master_item_id !== undefined)
    );

    // Created on 2026-02-07 by Farhan Khan - Filter mounting structure items using MountingStructureForm.jsx field structure
    // Valid if has roof_type OR structure_height OR special_structure_type OR combo_panels OR valid parts
    const sections = mountingStructureItems?.sections || [];
    const validMountingStructureItems = sections.filter(item => {
      if (!item) return false;
      const hasRoofType = item.roof_type && (typeof item.roof_type === 'string' ? item.roof_type.trim() !== '' : item.roof_type);
      const hasStructureHeight = item.structure_height_front || item.structure_height_back;
      const hasSpecialStructure = (item.special_structure_type && item.special_structure_type.trim && item.special_structure_type.trim() !== '') || (item.section_name === "SPECIAL STRUCTURE" && item.items && item.items.length > 0);
      const hasComboPanels = item.combo_panels_rcc || item.combo_panels_ground || item.combo_panels_tinshed || (item.roof_type === "COMBO" && item.items && item.items.length > 0);
      const hasValidParts = (item.parts && Array.isArray(item.parts) && item.parts.length > 0 &&
        item.parts.some(part => part && part.component_name && part.component_name.trim && part.component_name.trim())) ||
        (item.components && Array.isArray(item.components) && item.components.length > 0);
      return hasRoofType || hasStructureHeight || hasSpecialStructure || hasComboPanels || hasValidParts;
    });

    // Created on 2026-02-07 by Farhan Khan - Process owner_ids like Lead module
    // Convert from React Select format ({value, label}) to UUID array
    let processedOwnerIds = [];
    if (quotation.owner_ids && Array.isArray(quotation.owner_ids)) {
      processedOwnerIds = quotation.owner_ids
        .filter((item) => item && item.value)
        .map((item) => item.value);
    }

    const quotationData = {
      ...quotation,
      estimate_number: estimateNumber, // Use auto-generated or existing value
      quotation_date: quotation.quotation_date ? formatDate(quotation.quotation_date) : null,
      expiry_date: quotation.expiry_date ? formatDate(quotation.expiry_date) : null,
      owner_ids: processedOwnerIds, // Created on 2026-02-07 by Farhan Khan - UUID array format

      // Created on 2026-03-24 by Farhan Khan - Include Solar Financial Fields
      project_cost: quotation.project_cost || quotation.subtotal,
      capacity_phase: quotationLineItems[0]?.plant_capacity_required_by_client || quotationLineItems[0]?.quantity || "",
      rate_per_kw: quotationLineItems[0]?.unit_price || 0,
      gst_type: quotation.gst_type || "Inclusive",
      gst_amount: quotation.tax_gst,
      subsidy_amount: quotation.subsidy_amount,
      total_amount: quotation.grand_total,
      payable_amount: quotation.payable_amount || quotation.grand_total,
      effective_price: quotation.effective_price ?? (quotation.grand_total - (quotation.subsidy_amount ?? 0)),
      financial_product_name: quotationLineItems[0]?.product_name || "",

      items: allItems,
      quotation_bom_items: validBomItems.map((item, index) => ({
        bom_master_item_id: item.bom_master_item_id || null,
        item_name: item.item_name || "",
        description: item.description || "",
        specification: item.specification || "",
        category: item.category || [],
        size_range_capacity: item.size_range_capacity || "",
        make: item.make || [],
        unit: item.unit || "Nos",
        quantity: item.quantity || "",
        manufacturer_warranty: item.manufacturer_warranty || "",
        status: item.status || "Active",
        display_category: item.display_category || "Manual",
        sort_order: item.sort_order || (index + 1),
        sr_no: item.sr_no || (index + 1),
        section: item.section || item.display_category || "",
        sequence: item.sequence || (index + 1),
        specs: [], // Do NOT store IDs as per requirement
      }))
    };

    console.log("SUBSIDY STATE AT SAVE:", quotation.subsidy_amount);
    console.log("FINAL QUOTATION PAYLOAD TO DB:", quotationData);
    console.log("FINAL LINE ITEMS WITH SUBSIDY:", quotationData.items.filter(i => i.unit_price));

    // BUG FIX: Refactored save flow to use separate APIs
    // Added By Farhan Khan Khan on 24-Dec-2025
    // WHY IT WAS FAILING: Line items and BOM items were being processed in quotation route but errors were being swallowed
    // WHAT WAS FIXED: Sequential API calls - quotation header first, then line items, then BOM items

    let quotationId = null;
    let result = {};

    // STEP 1: Save quotation header (CREATE or UPDATE)
    if (quotation.id) {
      // Keep customer_name (Project) for persistence - was previously deleted
      delete quotationData.customer_mobile;
      delete quotationData.customer_state;
      delete quotationData.customer_city
      // UPDATE: Save quotation header
      result = await solisPowerApi.saveQuotation(quotationData);
      if (result && result.success) {
        quotationId = quotation.id;

        // Updated By System on 2026-04-29 - Event-Based Project Status Update
        // If Status is changed to Approved or Sent, update Project Status
        if (quotation.lead_id) {
          try {
            if (quotationData.status === "Approved") {
              await solisPowerApi.updateProjectStatusesByLeadId(quotation.lead_id, "Final Quotation");
            } else if (quotationData.status === "Sent") {
              await solisPowerApi.updateProjectStatusesByLeadId(quotation.lead_id, "Quotation Shared");
            }
          } catch (err) {
            console.warn("[QuotationEdit] Auto-status update failed:", err);
          }
        }
      } else {
        PubSub.publish("RECORD_ERROR_TOAST", {
          title: "Error",
          message: result?.errors || "Failed to update quotation",
        });
        return;
      }
    } else {
      // CREATE: Save quotation header and get quotation_id
      // Keep customer_name (Project) for persistence
      delete quotationData.customer_mobile;
      delete quotationData.customer_state;
      delete quotationData.customer_city
      result = await solisPowerApi.createQuotation(quotationData);
      if (result != null && result.success) {
        quotationId = result.quotation_id || result.id;
        if (quotation.lead_id) {
          try {
            // Updated By System on 2026-04-29 - Event-Based Project Status Update
            // 1. Set lead status to Negotiation
            await solisPowerApi.saveLead({ id: quotation.lead_id, lead_status: "Negotiation/ Followup" });

            // 2. Set project status based on Site Visit evidence
            const visits = await solisPowerApi.fetchUserTrackingByParentId(quotation.lead_id);
            const hasVisit = (visits || []).some(v => v.visit_status === "Complete");
            const newStatus = hasVisit ? "Quotation Shared" : "Quotation without Site Visit";

            await solisPowerApi.updateProjectStatusesByLeadId(quotation.lead_id, newStatus);

            // Additional check for Approved/Sent status during create
            if (quotationData.status === "Approved") {
              await solisPowerApi.updateProjectStatusesByLeadId(quotation.lead_id, "Final Quotation");
            } else if (quotationData.status === "Sent") {
              await solisPowerApi.updateProjectStatusesByLeadId(quotation.lead_id, "Quotation Shared");
            }
          } catch (e) {
            console.warn("Project status update after quotation create failed:", e);
          }
        }
      } else {
        PubSub.publish("RECORD_ERROR_TOAST", {
          title: "Error",
          message: result?.errors || "Failed to create quotation",
        });
        return;
      }
    }

    // STEP 2: Save quotation line items (if any)
    if (allItems && allItems.length > 0) {
      const lineItemsResult = await solisPowerApi.saveQuotationLineItems(quotationId, allItems);
      if (lineItemsResult?.errors) {
        PubSub.publish("RECORD_ERROR_TOAST", {
          title: "Error",
          message: `Failed to save line items: ${lineItemsResult?.errors}`,
        });
        return;
      }
    }

    // STEP 3: Save quotation BOM items (if any) - STANDARD BOM only
    // Created on 2026-02-07 by Farhan Khan - Filter empty rows
    if (validBomItems && validBomItems.length > 0) {
      const sortedBom = [...validBomItems].sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999));
      const finalBomItems = sortedBom.map((item, index) => ({
        ...item,
        bom_master_item_id: item.bom_master_item_id || null,
        item_name: item.item_name || "",
        description: item.description || "",
        specification: item.specification !== undefined && item.specification !== null ? item.specification : "",
        // ✅ STEP 3: LOCK PAYLOAD (FINAL CONTROL POINT)
        category: (item.category || []).map(val =>
          typeof val === "object" ? val.label : val
        ),
        make: (item.make || []).map(val =>
          typeof val === "object" ? val.label : val
        ),
        size_range_capacity: item.size_range_capacity || "",
        quantity: item.quantity || "",
        unit: item.unit || "Nos",
        brand_name: item.brand_name || "",
        manufacturer_warranty: item.manufacturer_warranty || "",
        display_category: item.display_category || item.section || "Standard",
        section: item.section || item.display_category || "Standard", // Store section for grouping
        sequence: index, // Preserve sequence
        sort_order: item.sort_order || (index + 1),
        sr_no: item.sr_no ?? (index + 1),
        specs: Array.isArray(item.specs) ? item.specs : [],
      }));

      const payload = {
        ...quotationData,
        bomItems: finalBomItems
      }
      console.log("FINAL PAYLOAD", payload.bomItems)
      // 💥 STEP 4: DEBUG (MANDATORY)
      console.log("FINAL CATEGORY", finalBomItems.map(i => i.category))
      console.log("FINAL MAKE", finalBomItems.map(i => i.make))

      const bomItemsResult = await solisPowerApi.saveQuotationBomItems(quotationId, finalBomItems);
      if (bomItemsResult?.errors) {
        PubSub.publish("RECORD_ERROR_TOAST", {
          title: "Error",
          message: `Failed to save BOM items: ${bomItemsResult?.errors}`,
        });
        return;
      }
    }

    // ✅ STEP 4: Save quotation mounting structure items (FINAL FIX - NO DATA LOSS)
    let finalMountingSource = mountingStructureItems?.sections || (Array.isArray(mountingStructureItems) ? mountingStructureItems : []);

    // Safety Snapshot Recovery: Prevent overwriting with empty defaults if original data existed
    if (quotationId && (!finalMountingSource || finalMountingSource.length === 0)) {
      if (originalMountingStructure && originalMountingStructure.length > 0) {
        finalMountingSource = originalMountingStructure;
      }
    }

    if (finalMountingSource && finalMountingSource.length > 0) {
      const toString = (arr) => {
        if (!arr) return "";
        if (Array.isArray(arr)) return arr.map(v => String(v).trim()).filter(Boolean).join(", ");
        return String(arr).trim();
      };

      const getLabelFromOptions = (value, options) => {
        if (!value) return "";
        const found = (options || []).find(opt => String(opt.value) === String(value));
        return found ? found.label : value;
      };

      const toNumber = (val) => {
        if (val === null || val === undefined || val === "") return null;
        const parsed = parseFloat(val);
        return isNaN(parsed) ? null : parsed;
      };

      const finalMountingRows = [];

      // ✨ FIX: ALL sections must be serialized without filtering
      const sectionsToProcess = finalMountingSource || [];

      sectionsToProcess.forEach((section, sIdx) => {
        const joinArr = (arr) => (Array.isArray(arr) ? arr.join(',') : (arr || ""));

        const coreRow = {
          quotation_id: quotationId,
          section_uuid: section.section_uuid || section.id || uuidv4(),
          section_name: section.section_name || section.roof_type,
          roof_type: section.roof_type,
          structure_height_front: toNumber(section.structure_height_front),
          structure_height_back: toNumber(section.structure_height_back),
          tin_shed_mounting_type: joinArr(section.tin_shed_mounting_type || section.selected_mounting_type),
          tin_shed_structure_type: joinArr(section.tin_shed_structure_type || section.selected_structure_type),
          combo_panels_rcc: toNumber(section.combo_panels_rcc),
          combo_panels_ground: toNumber(section.combo_panels_ground),
          combo_panels_tinshed: toNumber(section.combo_panels_tinshed),
          combo_roof_type: section.combo_roof_type || "",
          special_structure_type: (section.components || section.items || section.parts || [])[0]?.special_structure_type || section.special_structure_type || "",
          special_structure_specs: (section.components || section.items || section.parts || [])[0]?.special_structure_specs || section.special_structure_specs || section.specs || "",
          special_structure_length: toNumber((section.components || section.items || section.parts || [])[0]?.special_structure_length || section.special_structure_length || section.length),
          cleaning_system: section.cleaning_system || section.notes || "",
          sort_order: sIdx + 1,
          status: section.status || "Active",
        };

        const sectionParts = section.components || section.items || section.parts || [];
        coreRow.parts = sectionParts.map((row, rIdx) => {
          const resolvedMake = Array.isArray(row.make)
            ? row.make.map(m => (typeof m === 'object' ? m.label : m)).filter(Boolean).join(", ")
            : (row.make?.label || row.make || "");

          return {
            ...row,
            quotation_id: quotationId,
            item_name: row.item_name || "",
            specification: row.specification || "",
            special_structure_type: row.special_structure_type || section.special_structure_type || "",
            special_structure_specs: row.special_structure_specs || section.special_structure_specs || row.specification || "",
            special_structure_length: row.special_structure_length ?? section.special_structure_length ?? "",
            size: row.size || "",
            shape: toString(row.shape),
            make: resolvedMake,
            quantity: toNumber(row.quantity || 0),
            rate: toNumber(row.rate || 0),
            amount: toNumber(row.amount || 0),
            no_of_panels: toNumber(row.no_of_panels || 0),
            sort_order: rIdx + 1,
          };
        });

        finalMountingRows.push(coreRow);
      });

      console.log("FINAL MOUNTING SAVE PAYLOAD (FIXED NUMERIC)", finalMountingRows);

      try {
        const result = await solisPowerApi.saveQuotationMountingStructures(quotationId, finalMountingRows);
        if (!result?.success) {
          console.error("Mounting Save Error (Incomplete):", result?.errors);
        }
      } catch (error) {
        console.error('[QuotationEdit] Error saving mounting structures:', error);
      }
    }

    // STEP 5: Success - redirect to detail page
    PubSub.publish("RECORD_SAVED_TOAST", {
      title: "Record Saved",
      message: quotation.id ? "Quotation updated successfully" : "Quotation created successfully",
    });
    navigate(`/quotations/${quotationId}`, { state: { id: quotationId } });
  };

  const handleCancel = () => {
    if (quotation.id) {
      navigate(`/quotations/${quotation.id}`);
    } else {
      navigate(`/quotations`);
    }
  };

  return (
    <Main>
      <>
        <Helmet>
          <title>{quotation.id ? "Edit Quotation" : "Create Quotation"}</title>
        </Helmet>
      </>
      <Container fluid className="view-form">
        <Row className="mb-2">
          <Col lg={12}>
            <Link className="nav-link" to="/quotations">
              Home <i className="fa-solid fa-chevron-right"></i>{" "}
              <div style={{ color: "#23468c", display: "inline" }}>
                Quotations
              </div>
            </Link>
          </Col>
        </Row>
        <Row className="g-0">
          <Col lg={12} md={12} sm={12}>
            <Form
              className="mt-3"
              onSubmit={handleSubmit}
              noValidate
              validated={validated}
            >
              <Row className="view-form-header" style={{
                backgroundColor: "#364794",
                padding: "1rem 1.5rem",
                borderRadius: "8px 8px 0 0",
                marginBottom: "1.5rem"
              }}>
                <Col lg={8} md={8} sm={12}>
                  <h5 className="mb-0" style={{
                    color: "#fff",
                    fontWeight: "600",
                    fontSize: "1.25rem"
                  }}>
                    {quotation.id ? "Edit Quotation" : "Create Quotation"}
                  </h5>
                </Col>
                <Col lg={4} md={4} sm={12} className="d-flex gap-2 justify-content-end mt-2 mt-lg-0">
                  <Button
                    size="sm"
                    className="btn-save"
                    onClick={handleSubmit}
                    style={{
                      padding: "0.5rem 1.25rem",
                      fontWeight: "500",
                      borderRadius: "6px",
                      transition: "all 0.3s ease",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
                    }}
                  >
                    <i className="fa fa-save me-2"></i>Save Quotation
                  </Button>
                  <Button
                    size="sm"
                    className="btn-cancel"
                    onClick={handleCancel}
                    style={{
                      padding: "0.5rem 1.25rem",
                      fontWeight: "500",
                      borderRadius: "6px",
                      transition: "all 0.3s ease"
                    }}
                  >
                    <i className="fa fa-times me-2"></i>Cancel
                  </Button>
                </Col>
              </Row>

              {/* Part 1: Lead Name binding - read-only when Create Quotation from Lead View */}
              {(boundLead || (quotation?.lead_id && selectedLeadOption)) && (
                <Row className="mb-3">
                  <Col lg={12}>
                    <div style={{
                      padding: "0.75rem 1rem",
                      backgroundColor: "#f8f9fa",
                      borderRadius: "6px",
                      border: "1px solid #dee2e6"
                    }}>
                      <Form.Label className="mb-0" style={{ fontWeight: "600", color: "#495057", fontSize: "0.85rem" }}>
                        Lead Name
                      </Form.Label>
                      <div style={{ fontSize: "1rem", color: "#212529", marginTop: "0.25rem" }}>
                        {boundLead?.lead_name || selectedLeadOption?.label || ""}
                      </div>
                    </div>
                  </Col>
                </Row>
              )}

              <Tabs
                activeKey={activeTab}
                onSelect={(k) => setActiveTab(k)}
                className="mb-3 mt-2"
              >
                <Tab eventKey="quotation" title="Quotation">
                  <Row className="ibs-edit-form pb-3 mt-3" style={{
                    backgroundColor: "#fff",
                    borderRadius: "8px",
                    padding: "1.5rem",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.08)"
                  }}>
                    <Row lg={12} className="g-3">
                      {/* <Col lg={6} md={6} sm={12}>
                        <Form.Group className="mb-3">
                          <Form.Label className="form-view-label" style={{
                            fontWeight: "500",
                            color: "#495057",
                            marginBottom: "0.5rem",
                            fontSize: "0.9rem"
                          }}>
                            Lead / Customer
                          </Form.Label>
                          <Select
                            placeholder="Select Lead..."
                            onChange={handleLeadChange}
                            options={leadOptions}
                            value={leadOptions.find(l => l.value === quotation.lead_id)}
                            isClearable
                            styles={{
                              control: (base, state) => ({
                                ...base,
                                minHeight: "38px",
                                height: "38px",
                                borderColor: state.isFocused ? "#364794" : "#ced4da",
                                boxShadow: state.isFocused ? "0 0 0 0.2rem rgba(54, 71, 148, 0.25)" : "none",
                                borderRadius: "6px",
                                "&:hover": {
                                  borderColor: "#364794"
                                }
                              }),
                            }}
                          />
                        </Form.Group>
                      </Col> */}
                      {/* <Col lg={6} md={6} sm={12}>
  <Form.Group className="mb-3">
    <Form.Label className="form-view-label">
      Lead / Customer
    </Form.Label>

    <Select
      placeholder="Select Lead..."
      options={leadOptions}

      value={selectedLeadOption}

      onChange={(opt) => {
        setSelectedLeadOption(opt);
        handleLeadChange(opt);
      }}

      isClearable
      isDisabled={!!quotation?.id}

      styles={{
        control: (base, state) => ({
          ...base,
          minHeight: "38px",
          borderColor: state.isFocused ? "#364794" : "#ced4da",
          boxShadow: state.isFocused
            ? "0 0 0 0.2rem rgba(54,71,148,.25)"
            : "none",
          borderRadius: "6px",
          "&:hover": { borderColor: "#364794" }
        }),
      }}
    />
  </Form.Group>
                      </Col> */}

                      <Col lg={6} md={6} sm={12}>
                        <Form.Group className="mb-3">
                          <Form.Label
                            className="form-view-label"
                            style={{
                              fontWeight: "500",
                              color: "#495057",
                              marginBottom: "0.5rem",
                              fontSize: "0.9rem"
                            }}
                          >
                            Lead / Customer
                          </Form.Label>
                          {/* <Select
                        placeholder="Select Lead..."
                        options={leadOptions}
                        value={selectedLeadOption}

                        onChange={(opt) => {
                          setSelectedLeadOption(opt);
                          handleLeadChange(opt);
                        }}

                        isClearable
                        isDisabled={!!quotation.id}
                        // styles={customSelectStyles}
                        styles={{
                              control: (base, state) => ({
                                ...base,
                                minHeight: "38px",
                                borderColor: state.isFocused ? "#364794" : "#ced4da",
                                boxShadow: state.isFocused ? "0 0 0 0.2rem rgba(54, 71, 148, 0.25)" : "none",
                                borderRadius: "6px",
                                "&:hover": {
                                  borderColor: "#364794"
                                }
                              }),
                            }}
                      /> */}

                          <Select
                            placeholder="Select Lead..."
                            options={leadOptions}
                            value={selectedLeadOption}
                            onChange={handleLeadChange}
                            isClearable
                            // isMulti - Lead editable on Edit; dependent dropdowns update via existing logic
                            styles={{
                              control: (base, state) => ({
                                ...base,
                                minHeight: "38px",
                                borderColor: state.isFocused ? "#364794" : "#ced4da",
                                boxShadow: state.isFocused
                                  ? "0 0 0 0.2rem rgba(54, 71, 148, 0.25)"
                                  : "none",
                                borderRadius: "6px",
                                "&:hover": { borderColor: "#364794" }
                              }),
                            }}
                          />
                        </Form.Group>
                      </Col>

                      {/* Created on 2026-02-07 by Farhan Khan - Assigned Users dropdown (same pattern as Lead module) */}
                      <Col lg={6} md={6} sm={12}>
                        <Form.Group className="mb-3">
                          <Form.Label
                            className="form-view-label"
                            htmlFor="formBasicQuotationAssignedUsers"
                            style={{
                              fontWeight: "500",
                              color: "#495057",
                              marginBottom: "0.5rem",
                              fontSize: "0.9rem"
                            }}
                          >
                            Assign Users
                          </Form.Label>
                          <Select
                            disabled={false}
                            className={`${Array.isArray(quotation?.owner_ids) &&
                              quotation?.owner_ids?.length > 0
                              ? "custom-select-valid"
                              : "custom-select-invalid"
                              }`}
                            isMulti
                            placeholder="Select your Assign Staff"
                            options={assignUsers?.map((item) => ({
                              value: item.id,
                              label:
                                item.userrole === "SUPER_ADMIN"
                                  ? item.username
                                  : item.userrole === "ADMIN"
                                    ? `${item.username} - (${item.user_type}) - (Manager)`
                                    : `${item.username} - (${item.user_type})`,
                            }))}
                            value={quotation?.owner_ids?.filter(
                              (item) => item.value
                            )}
                            onChange={(e) => {
                              setQuotation(prev => ({
                                ...prev,
                                owner_ids: e || []
                              }));
                            }}
                            styles={{
                              control: (base, state) => ({
                                ...base,
                                minHeight: "38px",
                                borderColor: state.isFocused ? "#364794" : "#ced4da",
                                boxShadow: state.isFocused ? "0 0 0 0.2rem rgba(54, 71, 148, 0.25)" : "none",
                                borderRadius: "6px",
                                "&:hover": {
                                  borderColor: "#364794"
                                }
                              }),
                            }}
                          />
                        </Form.Group>
                      </Col>
                      {/* Estimate Number - Always Visible but Read Only */}
                      <Col lg={6} md={6} sm={12}>
                        <Form.Group className="mb-3">
                          <Form.Label
                            className="form-view-label"
                            style={{
                              fontWeight: "500",
                              color: "#495057",
                              marginBottom: "0.5rem",
                              fontSize: "0.9rem"
                            }}
                          >
                            Estimate Number
                          </Form.Label>
                          <Form.Control
                            type="text"
                            name="estimate_number"
                            value={quotation.estimate_number || ""}
                            readOnly
                            disabled
                            style={{
                              borderRadius: "6px",
                              borderColor: "#ced4da",
                              backgroundColor: "#f8f9fa"
                            }}
                          />
                        </Form.Group>
                      </Col>
                      <Col lg={6} md={6} sm={12}>
                        <Form.Group className="mb-3">
                          <Form.Label className="form-view-label" style={{
                            fontWeight: "500",
                            color: "#495057",
                            marginBottom: "0.5rem",
                            fontSize: "0.9rem"
                          }}>
                            Quotation Date
                          </Form.Label>
                          <Form.Control
                            type="date"
                            name="quotation_date"
                            value={quotation.quotation_date}
                            onChange={handleQuotationChange}
                            style={{
                              borderRadius: "6px",
                              borderColor: "#ced4da"
                            }}
                          />
                        </Form.Group>
                      </Col>
                      <Col lg={6} md={6} sm={12}>
                        <Form.Group className="mb-3">
                          <Form.Label className="form-view-label" style={{
                            fontWeight: "500",
                            color: "#495057",
                            marginBottom: "0.5rem",
                            fontSize: "0.9rem"
                          }}>
                            Expiry Date
                          </Form.Label>
                          <Form.Control
                            type="date"
                            name="expiry_date"
                            value={quotation.expiry_date}
                            onChange={handleQuotationChange}
                            style={{
                              borderRadius: "6px",
                              borderColor: "#ced4da"
                            }}
                          />
                        </Form.Group>
                      </Col>
                      <Col lg={6} md={6} sm={12}>
                        <Form.Group className="mb-3">
                          <Form.Label className="form-view-label" style={{
                            fontWeight: "500",
                            color: "#495057",
                            marginBottom: "0.5rem",
                            fontSize: "0.9rem"
                          }}>
                            Status
                          </Form.Label>
                          <Form.Select
                            name="status"
                            value={quotation.status}
                            onChange={handleQuotationChange}
                            style={{
                              borderRadius: "6px",
                              borderColor: "#ced4da"
                            }}
                          >
                            <option value="Draft">Draft</option>
                            <option value="Sent">Sent</option>
                            <option value="Approved">Approved</option>
                            <option value="Rejected">Rejected</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col lg={6} md={6} sm={12}>
                        <Form.Group className="mb-3">
                          <Form.Label className="form-view-label" style={{
                            fontWeight: "500",
                            color: "#495057",
                            marginBottom: "0.5rem",
                            fontSize: "0.9rem"
                          }}>
                            Project
                          </Form.Label>
                          {quotation?.lead_id ? (
                            <Select
                              placeholder={projectOptions.length === 0 ? "No projects for this lead" : "Select Project..."}
                              options={projectOptions}
                              value={selectedProjectOption}
                              onChange={handleProjectChange}
                              isClearable
                              styles={{
                                control: (base, state) => ({
                                  ...base,
                                  minHeight: "38px",
                                  borderColor: state.isFocused ? "#364794" : "#ced4da",
                                  boxShadow: state.isFocused ? "0 0 0 0.2rem rgba(54, 71, 148, 0.25)" : "none",
                                  borderRadius: "6px",
                                  "&:hover": { borderColor: "#364794" }
                                }),
                              }}
                            />
                          ) : (
                            <Form.Control
                              type="text"
                              placeholder="Select Lead first"
                              value=""
                              readOnly
                              disabled
                              style={{ borderRadius: "6px", backgroundColor: "#e9ecef" }}
                            />
                          )}
                          {quotation?.lead_id && projectOptions.length === 0 && (
                            <Form.Text className="text-muted">No project line items found for this lead.</Form.Text>
                          )}
                        </Form.Group>
                      </Col>
                      <Col lg={6} md={6} sm={12}>
                        <Form.Group className="mb-3">
                          <Form.Label className="form-view-label" style={{
                            fontWeight: "500",
                            color: "#495057",
                            marginBottom: "0.5rem",
                            fontSize: "0.9rem"
                          }}>
                            Customer Mobile
                          </Form.Label>
                          <Form.Control
                            type="text"
                            name="customer_mobile"
                            placeholder="Enter Mobile"
                            value={quotation.customer_mobile}
                            onChange={handleQuotationChange}
                            style={{
                              borderRadius: "6px",
                              borderColor: "#ced4da"
                            }}
                          />
                        </Form.Group>
                      </Col>
                      <Col lg={6} md={6} sm={12}>
                        <Form.Group className="mb-3">
                          <Form.Label className="form-view-label" style={{
                            fontWeight: "500",
                            color: "#495057",
                            marginBottom: "0.5rem",
                            fontSize: "0.9rem"
                          }}>
                            State
                          </Form.Label>
                          <Form.Control
                            type="text"
                            name="customer_state"
                            placeholder="Enter State"
                            value={quotation.customer_state}
                            onChange={(e) => handleUppercaseChange(e, handleQuotationChange)}
                            style={{
                              borderRadius: "6px",
                              borderColor: "#ced4da"
                            }}
                          />
                        </Form.Group>
                      </Col>
                      <Col lg={6} md={6} sm={12}>
                        <Form.Group className="mb-3">
                          <Form.Label className="form-view-label" style={{
                            fontWeight: "500",
                            color: "#495057",
                            marginBottom: "0.5rem",
                            fontSize: "0.9rem"
                          }}>
                            City
                          </Form.Label>
                          <Form.Control
                            type="text"
                            name="customer_city"
                            placeholder="Enter City"
                            value={quotation.customer_city}
                            onChange={(e) => handleUppercaseChange(e, handleQuotationChange)}
                            style={{
                              borderRadius: "6px",
                              borderColor: "#ced4da"
                            }}
                          />
                        </Form.Group>
                      </Col>
                      <Col lg={12}>
                        <Form.Group className="mb-3">
                          <Form.Label className="form-view-label" style={{
                            fontWeight: "500",
                            color: "#495057",
                            marginBottom: "0.5rem",
                            fontSize: "0.9rem"
                          }}>
                            Payment Terms
                          </Form.Label>
                          <ReactQuill
                            theme="snow"
                            value={quotation.payment_terms || ""}
                            onChange={(value) => setQuotation(prev => ({ ...prev, payment_terms: value }))}
                            placeholder="Enter payment terms..."
                            style={{ minHeight: "120px", marginBottom: "3rem" }}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </Row>

                  {/* Financial Line Items - Now with embedded project calculations */}
                  <QuotationLineItems
                    items={quotationLineItems}
                    setItems={setQuotationLineItems}
                    productOptions={nonMaterialProductOptions}
                    quotation={quotation}
                    setQuotation={setQuotation}
                  />
                </Tab>

                <Tab eventKey="bom" title="Bill of Materials" >
                  {/* Added By Farhan Khan Khan on 24-Dec-2025 - New Quotation BOM Items */}
                  <QuotationBomItems
                    quotationId={quotation.id}
                    quotationBomItems={quotationBomItems}
                    setQuotationBomItems={setQuotationBomItems}
                    brandOptions={brandOptions}
                    activeTab={activeTab}
                    // Issue 2: Undo/Redo props
                    bomHistory={bomHistory}
                    bomFuture={bomFuture}
                    saveToBomHistory={saveToBomHistory}
                    undoBomChange={undoBomChange}
                    redoBomChange={redoBomChange}
                  />

                  {/* Keep old BOM section for backward compatibility (hidden/disabled) */}
                  {/* <BomSection
                    sections={bomSections}
                    setSections={setBomSections}
                    productOptions={materialProductOptions}
                    brandOptions={brandOptions}
                  /> */}
                </Tab>

                {/* Created on 2026-02-07 by Farhan Khan - Mounting Structure Tab */}
                {/* Uses same structure and fields as BOM but separate state */}
                <Tab eventKey="mounting" title="Mounting Structure">
                  <QuotationMountingStructures
                    quotationId={quotation.id}
                    mountingStructureItems={mountingStructureItems}
                    setMountingStructureItems={setMountingStructureItems}
                    brandOptions={brandOptions}
                    activeTab={activeTab}
                  />
                </Tab>
              </Tabs>
            </Form>
          </Col>
        </Row>
      </Container>
    </Main>
  );
};

export default QuotationEdit;